import { ItemBar } from "components/ItemBar";
import { ToastContext } from "context/ToastContext";
import { useUserWithAccount } from "context/UserAccountProvider";
import { Button } from "primereact/button";
import { useContext, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  useUpdateAccountMutation,
  useUpsertBenefitSelectionRulesMutation,
  useUpsertPayerAccountFeesMutation,
} from "store/queries/account";

import { validaTeCustomPaymentPlanSettings } from "../helpers/helpers";
import { mapFormToRequestBody } from "../helpers/mapFormToRequestBody";
import { rulesInSpecificityOrder } from "../helpers/rulesInSpecificityOrder";
import { wasFieldEdited } from "../helpers/wasFieldEdited";
import { toBenefitSelectionRulePayload } from "./BenefitSelection/mapper";

export function SubmitEdit({
  id,
  initialAccountLocations = [],
}: {
  id: string;
  initialAccountLocations?: Array<{ id: string; name?: string }>;
}) {
  const [useUpdateAccount] = useUpdateAccountMutation();
  const [upsertPayerAccountFees] = useUpsertPayerAccountFeesMutation();
  const [upsertBenefitSelectionRules] =
    useUpsertBenefitSelectionRulesMutation();
  const { accountRefetch } = useUserWithAccount();
  const [buttonLoading, setButtonLoading] = useState(false);
  const { current: toastElement } = useContext(ToastContext);
  const {
    getValues,
    formState: { isValid, dirtyFields },
    watch,
  } = useFormContext();
  /*
    CLE-2792 — read during render on purpose. `formState` is a proxy that subscribes only to the
    parts a component touches while rendering, so reading `dirtyFields` inside the click handler
    would leave this component un-subscribed and the flag frozen at its mount value.
  */
  const benefitSelectionRulesEdited = wasFieldEdited(
    dirtyFields.benefitSelectionRules
  );
  const paymentPlanCustomSettingsField = watch("paymentPlanCustomSettings");
  const navigate = useNavigate();

  const updateHospital = async () => {
    const formValues = getValues();
    const requestBody = await mapFormToRequestBody(formValues, {
      initialAccountLocations,
    });
    setButtonLoading(true);

    try {
      // Update the main account
      await useUpdateAccount({ ...requestBody, id }).unwrap();

      // We always update payer account fees (even if empty array to clear existing fees)
      const formattedPayerAccountFees = (formValues.payerAccountFees || []).map(
        (fee: any) => ({
          payerId: parseInt(fee.payerId, 10),
          type: fee.type || "ClearPatientFee",
          valueInPercentage: parseFloat(fee.valueInPercentage) || 0,
        })
      );

      await upsertPayerAccountFees({
        accountId: id,
        payerAccountFees: formattedPayerAccountFees,
      }).unwrap();

      /*
        CLE-2683 — sent whenever the payer-filters table on this form was edited, including when it
        was edited down to nothing, so removing the last rule genuinely clears them.

        🚨 AND NOT SENT AT ALL WHEN NOBODY TOUCHED IT. These rows have a second editor — the
        benefit-configuration card PUTs them with the rest of the engine configuration — and two
        savers of the same rows means the later button wins: saving a filter in the card and then
        pressing "Update Hospital" for some unrelated account change would silently restore the
        pre-edit list. An untouched table has nothing to write, so it writes nothing. This applies to
        EVERYBODY, not only the people who can see the card: it is a statement about the form, and a
        re-send of a list nobody edited was never worth a request from anyone.

        ⚠️ `dirtyFields.benefitSelectionRules` is a per-field array, TRUTHY EVEN WHEN NOTHING
        CHANGED — see `wasFieldEdited`, which is what makes this an edit check rather than a
        has-been-written-through check.

        🚨 SORTED BY TODAY'S EFFECTIVE PRECEDENCE, NOT BY DISPLAY ORDER. The API now writes
        `priority = index`, so saving this list in the order it happens to be displayed would
        re-rank every account whose rules are not already in specificity order — see
        `rulesInSpecificityOrder`.
      */
      if (benefitSelectionRulesEdited) {
        await upsertBenefitSelectionRules({
          accountId: id,
          rules: rulesInSpecificityOrder(
            formValues.benefitSelectionRules || []
          ).map(toBenefitSelectionRulePayload),
        }).unwrap();
      }

      toastElement?.show({
        severity: "success",
        summary: "Account updated",
        detail: "Account updated successfully",
      });
      setButtonLoading(false);
      navigate("/hospitals");
      accountRefetch();
    } catch (error: any) {
      console.error(error);
      setButtonLoading(false);
      const message =
        error?.data?.message ||
        error?.message ||
        error?.data?.messages?.join(", ") ||
        error?.data?.messages?.[0] ||
        "An error has occurred attempting to update the account";
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: message,
      });
    }
  };

  const submitButtonDIsabled = useMemo(() => {
    if (!isValid) return true;
    if (paymentPlanCustomSettingsField?.length) {
      const isCustomSettingsValid = validaTeCustomPaymentPlanSettings({
        settings: paymentPlanCustomSettingsField,
      });
      if (!isCustomSettingsValid) return true;
    }
    return false;
  }, [isValid, paymentPlanCustomSettingsField]);

  return (
    <ItemBar>
      <div
        data-testid="submit__Hospital"
        className="flex flex-1 justify-content-end"
      >
        <Button
          label="Update Hospital"
          onClick={updateHospital}
          disabled={submitButtonDIsabled}
          loading={buttonLoading}
        />
      </div>
    </ItemBar>
  );
}
